// ============================================================================
// CHANGO V5.0 — D5 Quote Detail (read-only + action buttons)
// ============================================================================
// Drives /app/quotes/:id. Role-aware rendering:
//   - boss   : sees cost/margin + Approve + Reject + Issue + Close + Edit
//   - finance: sees cost/margin + Reject + Issue + Close + Edit
//   - sales  : sees public totals only (no margin/cost), no buttons
//
// Two-person rule: Approve button is suppressed when the current user is the
// submitter, matching backend rejection (defensive).
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import { HideFromSales } from '../../components/RoleGate.jsx';
import { formatRmb, formatFx } from '../../lib/marginPreview.js';
import QuoteCommentPanel from '../../components/QuoteCommentPanel.jsx';
import TimelineFeed from '../../components/TimelineFeed.jsx';
import ShareManagerPanel from '../../components/ShareManagerPanel.jsx';

const STATUS_META = {
  draft:        { label: '草稿',    tone: 'bg-slate-100 text-slate-700' },
  pending:      { label: '待审',    tone: 'bg-amber-100 text-amber-800' },
  approved:     { label: '已审',    tone: 'bg-emerald-100 text-emerald-800' },
  rejected:     { label: '已拒',    tone: 'bg-rose-100 text-rose-800' },
  issued:       { label: '已出具',  tone: 'bg-sky-100 text-sky-800' },
  closed_won:   { label: '成交',    tone: 'bg-green-100 text-green-800' },
  closed_lost:  { label: '流失',    tone: 'bg-slate-200 text-slate-600' },
  archived:     { label: '归档',    tone: 'bg-slate-100 text-slate-500' }
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || '—', tone: 'bg-slate-100 text-slate-700' };
  return (
    <span className={['inline-block rounded px-2 py-0.5 text-xs font-medium', meta.tone].join(' ')}>
      {meta.label}
    </span>
  );
}

function RejectDialog({ open, onClose, onSubmit }) {
  const [reason, setReason] = useState('');
  const [err, setErr] = useState(null);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="card max-w-lg w-full p-6">
        <h3 className="mb-2 text-lg font-semibold text-slate-800">拒绝报价</h3>
        <p className="mb-4 text-sm text-slate-500">请填写拒绝理由（4-1000 字）。</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={5}
          maxLength={1000}
          className="field"
          placeholder="例如：客户价格低于我司成本价 10%，建议重新谈判。"
        />
        {err && <p className="err">{err}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>取消</button>
          <button type="button" className="btn-primary"
            onClick={async () => {
              const trimmed = reason.trim();
              if (trimmed.length < 4) { setErr('理由至少 4 字'); return; }
              try { await onSubmit(trimmed); onClose(); }
              catch (e) { setErr(e.message || '提交失败'); }
            }}>
            确认拒绝
          </button>
        </div>
      </div>
    </div>
  );
}

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const role = user?.role;
  const isSales = role === 'sales';
  const isBoss = role === 'boss';
  const isFinance = role === 'finance';

  async function reload() {
    setErr(null);
    try {
      const d = await api.get(`/api/quotes/detail/${id}`);
      setDetail(d);
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.code}: ${e.message}` : (e.message || '加载失败'));
    }
  }

  useEffect(() => { reload(); }, [id]);

  async function act(path, body) {
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/api/quotes/${id}/${path}`, body ?? {});
      await reload();
    } catch (e) {
      setErr(e instanceof HttpError ? `${e.code}: ${e.message}` : (e.message || '操作失败'));
    } finally {
      setBusy(false);
    }
  }

  if (err && !detail) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-10">
        <div className="alert-error">{err}</div>
        <Link to="/app/quotes" className="btn-ghost mt-4">← 返回列表</Link>
      </div>
    );
  }
  if (!detail) {
    return <div className="px-6 py-10 text-center text-slate-400">加载中…</div>;
  }

  const status = detail.lifecycle_status || detail.status;
  const canEdit     = (isBoss || isFinance) && status === 'draft';
  const canSubmit   = (isBoss || isFinance) && status === 'draft';
  const canApprove  = isBoss && status === 'pending'
                      && Number(detail.submitted_by) !== Number(user?.id);
  const canReject   = (isBoss || isFinance) && (status === 'pending' || status === 'approved');
  const canIssue    = (isBoss || isFinance) && status === 'approved';
  const canClose    = (isBoss || isFinance) && status === 'issued';
  const canPrint    = ['approved', 'issued', 'closed_won', 'closed_lost'].includes(status);

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">
            {detail.code || `报价 #${detail.id}`}
          </h1>
          <p className="text-sm text-slate-500">
            <StatusPill status={status} /> · 客户：{detail.customer_name || '—'} · 负责人：{detail.owner_display_name || detail.owner_name || detail.owner_email || '—'}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/quotes" className="btn-ghost">← 返回列表</Link>
          {canPrint && (
            <>
              <Link to={`/app/quotes/${detail.id}/print`} className="btn-ghost">打印 / PDF</Link>
              <select
                className="field text-sm"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    window.open(`/api/quotes/${detail.id}/export-pdf?lang=${e.target.value}`, '_blank');
                    e.target.value = '';
                  }
                }}
              >
                <option value="">下载 PDF...</option>
                <option value="zh">中文版</option>
                <option value="en">英文版</option>
                <option value="bilingual">中英对照</option>
              </select>
              <button
                type="button"
                className="btn-ghost"
                disabled={isSales}
                title={isSales ? '销售员无权下载报价单 Excel' : ''}
                onClick={() => {
                  if (!isSales) {
                    window.open(`/api/quotes/${detail.id}/export-excel`, '_blank');
                  }
                }}
              >
                下载 Excel
              </button>
            </>
          )}
          {canEdit && (
            <Link to={`/app/quotes/${detail.id}/edit`} className="btn-primary">编辑</Link>
          )}
        </div>
      </header>

      {err && <div className="alert-error mb-4">{err}</div>}
      {detail.reject_reason && (
        <div className="alert-error mb-4">
          <strong>拒绝理由：</strong> {detail.reject_reason}
        </div>
      )}

      <section className="card mb-4 p-4">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">公开总额</h3>
        <dl className="grid grid-cols-2 gap-2 text-sm tabular-nums md:grid-cols-3">
          <Row label="币种" value={detail.currency || 'CNY'} />
          <Row label="汇率" value={detail.fx_rate_to_rmb?.toString() ?? '1'} />
          <Row label="折扣率" value={`${detail.discount_pct ?? 0}%`} />
          <Row label="流转税率"  value={`${detail.tax_rate_pct ?? 0}%`} />
          <Row label="销售小计"  value={formatRmb(detail.sell_subtotal_rmb)} />
          <Row label="折扣金额"  value={formatRmb(detail.discount_amount_rmb)} />
          <Row label="折扣后销售" value={formatRmb(detail.sell_after_discount_rmb)} />
          <Row label="流转税"      value={formatRmb(detail.tax_amount_rmb)} />
          <Row label="营业收入"  value={formatRmb(detail.total_revenue_rmb)} />
          {detail.currency !== 'CNY' && (
            <Row label={`外币金额 (${detail.currency})`}
              value={formatFx(detail.total_revenue_fx, detail.currency)} />
          )}
        </dl>
      </section>

      <HideFromSales>
        <section className="card mb-4 p-4 border-amber-200">
          <h3 className="mb-3 text-sm font-semibold text-amber-700">
            敏感：成本 / 利润（仅 boss / finance 可见）
          </h3>
          <dl className="grid grid-cols-2 gap-2 text-sm tabular-nums md:grid-cols-3">
            <Row label="成本小计" value={formatRmb(detail.cost_subtotal_rmb)} />
            <Row label="共担费用" value={formatRmb(detail.shared_cost_rmb)} />
            {Number(detail.channel_cost_rmb) > 0 && (
              <Row label="跨境渠道成本" value={formatRmb(detail.channel_cost_rmb)} />
            )}
            {Number(detail.entity_cost_rmb) > 0 && (
              <Row label="主体税费/合规成本" value={formatRmb(detail.entity_cost_rmb)} />
            )}
            <Row label="总成本"   value={formatRmb(detail.total_cost_rmb)} />
            {detail.profit_before_entity_rmb != null && Number(detail.entity_cost_rmb) > 0 && (
              <Row label="主体前利润" value={formatRmb(detail.profit_before_entity_rmb)} />
            )}
            <Row label="毛利金额" value={formatRmb(detail.gross_profit_rmb)} />
            {detail.group_net_profit_rmb != null && Number(detail.entity_cost_rmb) > 0 && (
              <Row label="集团净利润" value={formatRmb(detail.group_net_profit_rmb)} />
            )}
            <Row label="毛利率"
              value={detail.margin_pct == null ? '—' : `${Number(detail.margin_pct).toFixed(2)}%`} />
          </dl>
        </section>
      </HideFromSales>

      {!isSales && (detail.layer_c_data || detail.layer_e_data) && (
        <section className="card mb-4 p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">扩展决策参数</h3>
          <dl className="grid grid-cols-2 gap-2 text-sm tabular-nums md:grid-cols-3">
            {detail.layer_c_data && (
              <>
                <Row label="平台佣金" value={`${detail.layer_c_data.inputs?.platform_commission_pct ?? 0}%`} />
                <Row label="广告 ACOS" value={`${detail.layer_c_data.inputs?.ad_rate_pct ?? 0}%`} />
                <Row label="FBA/配送" value={formatRmb(detail.layer_c_data.breakdown?.fba_fulfillment_rmb)} />
              </>
            )}
            {detail.layer_e_data && (
              <>
                <Row label="海外利润占比" value={`${detail.layer_e_data.inputs?.overseas_profit_share_pct ?? 0}%`} />
                <Row label="海外所得税" value={formatRmb(detail.layer_e_data.split?.overseas_tax_cost_rmb)} />
                <Row label="海外运营主体利润" value={formatRmb(detail.layer_e_data.split?.overseas_profit_before_tax_rmb)} />
              </>
            )}
          </dl>
        </section>
      )}

      <section className="card mb-4">
        <h3 className="px-4 pt-4 text-sm font-semibold text-slate-700">商品行（共 {detail.lines?.length || 0} 条）</h3>
        <div className="overflow-x-auto p-2">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-2 text-left">SKU</th>
                <th className="px-2 py-2 text-left">描述</th>
                <th className="px-2 py-2 text-right">数量</th>
                <th className="px-2 py-2 text-right">单件售价</th>
                <HideFromSales><th className="px-2 py-2 text-right">单件成本</th></HideFromSales>
                <th className="px-2 py-2 text-right">销售小计</th>
                <HideFromSales><th className="px-2 py-2 text-right">毛利率</th></HideFromSales>
              </tr>
            </thead>
            <tbody>
              {(detail.lines || []).map((l) => (
                <tr key={l.idx} className="border-t border-slate-100">
                  <td className="px-2 py-1.5 font-mono text-xs">{l.sku}</td>
                  <td className="px-2 py-1.5">{l.description || '—'}</td>
                  <td className="px-2 py-1.5 text-right">{l.qty}</td>
                  <td className="px-2 py-1.5 text-right">{formatRmb(l.unit_sell_rmb)}</td>
                  <HideFromSales>
                    <td className="px-2 py-1.5 text-right">{formatRmb(l.unit_cost_rmb)}</td>
                  </HideFromSales>
                  <td className="px-2 py-1.5 text-right">{formatRmb(l.line_sell_rmb)}</td>
                  <HideFromSales>
                    <td className="px-2 py-1.5 text-right">
                      {l.line_margin_pct == null ? '—' : `${Number(l.line_margin_pct).toFixed(2)}%`}
                    </td>
                  </HideFromSales>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {!isSales && Array.isArray(detail.shared_costs) && detail.shared_costs.length > 0 && (
        <section className="card mb-4 p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">共担费用</h3>
          <ul className="divide-y divide-slate-100 text-sm">
            {detail.shared_costs.map((sc) => (
              <li key={sc.idx} className="flex items-center justify-between py-1.5">
                <span>{sc.label || `#${sc.idx}`}</span>
                <span className="tabular-nums">{formatRmb(sc.amount_rmb)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!isSales && (
        <footer className="flex flex-wrap gap-2">
          {canSubmit && (
            <button type="button" className="btn-primary"
              disabled={busy}
              onClick={() => act('submit')}>
              {busy ? '提交中…' : '提交审批'}
            </button>
          )}
          {canApprove && (
            <button type="button" className="btn-primary"
              disabled={busy}
              onClick={() => act('approve')}>
              {busy ? '处理中…' : '审批通过'}
            </button>
          )}
          {canReject && (
            <button type="button" className="btn-ghost text-rose-700 hover:bg-rose-50"
              disabled={busy}
              onClick={() => setRejectOpen(true)}>
              拒绝
            </button>
          )}
          {canIssue && (
            <button type="button" className="btn-primary"
              disabled={busy}
              onClick={() => act('issue')}>
              {busy ? '出具中…' : '出具报价单'}
            </button>
          )}
          {canClose && (
            <>
              <button type="button" className="btn-ghost"
                disabled={busy}
                onClick={() => act('close', { outcome: 'closed_won' })}>
                标记成交
              </button>
              <button type="button" className="btn-ghost"
                disabled={busy}
                onClick={() => act('close', { outcome: 'closed_lost' })}>
                标记流失
              </button>
            </>
          )}
        </footer>
      )}

      <RejectDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onSubmit={async (reason) => {
          await act('reject', { reason });
        }}
      />

      {/* D6 — comments + entity-scoped timeline */}
      <QuoteCommentPanel quoteId={id} />
      {/* D5.3 — share link manager (all roles may view / create per role rules) */}
      <ShareManagerPanel quoteId={id} />
      <TimelineFeed
        scope="entity"
        entityType="quote"
        entityId={id}
        pageSize={20}
        className="mt-6"
      />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between rounded bg-slate-50 px-3 py-1.5">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-800">{value}</dd>
    </div>
  );
}
